import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { errorDetails, errorMessage } from '../api';

type Kind = 'info' | 'success' | 'error' | 'warning';
interface ToastItem {
  id: number;
  kind: Kind;
  title: string;
  body?: string;
  lines?: string[];
}
interface ToastApi {
  push: (t: Omit<ToastItem, 'id'>) => void;
  success: (title: string, body?: string) => void;
  error: (e: unknown, title?: string) => void;
}

const Ctx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const next = useRef(1);
  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const push = useCallback(
    (t: Omit<ToastItem, 'id'>) => {
      const id = next.current++;
      setItems((xs) => [...xs.slice(-3), { ...t, id }]);
      setTimeout(() => dismiss(id), t.kind === 'error' || t.lines?.length ? 10000 : 5000);
    },
    [dismiss],
  );
  const value = useMemo<ToastApi>(
    () => ({
      push,
      success: (title, body) => push({ kind: 'success', title, body }),
      error: (e, title) =>
        push({ kind: 'error', title: title ?? errorMessage(e), body: title ? errorMessage(e) : undefined, lines: errorDetails(e) }),
    }),
    [push],
  );
  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="toasts" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`} role={t.kind === 'error' ? 'alert' : 'status'}>
            <div className="toast-text">
              <strong>{t.title}</strong>
              {t.body && <div>{t.body}</div>}
              {t.lines && t.lines.length > 0 && (
                <ul>
                  {t.lines.map((l, i) => (
                    <li key={i}>{l}</li>
                  ))}
                </ul>
              )}
            </div>
            <button onClick={() => dismiss(t.id)} aria-label="Dismiss notification">
              ×
            </button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  const t = useContext(Ctx);
  if (!t) throw new Error('useToast outside ToastProvider');
  return t;
}
